import React from "react";

import {
  useDataGrid,
  DataGrid,
  GridColumns,
  List,
  esES,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  Box,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from "@pankod/refine-mui";
import {
  IReportsSents,
  IProduct,
  Nullable,
  IReportsSentsFilterVariables,
} from "interfaces";
import { Controller, useForm } from "@pankod/refine-react-hook-form";
import { CrudFilters, getDefaultFilter, HttpError } from "@pankod/refine-core";
import { SearchOutlined } from "@mui/icons-material";

export const ReportsSentsList: React.FC = () => {
  const [headerHeight, setHeaderHeight] = React.useState<number>(0);

  const { dataGridProps, filters, setFilters } = useDataGrid<IProduct>({
    // resource: `finished-pieces/report/${date}`,
  });

  const columns = React.useMemo<GridColumns<IReportsSents>>(
    () => [
      {
        field: "order",
        headerName: "Ordenes",
        flex: 1,
      },
      {
        field: "status",
        headerName: "Estatus",
        flex: 1,
      },
      {
        field: "type",
        headerName: "Tipo",
        flex: 1,
      },
      {
        field: "total_pieces",
        headerName: "Pz. totales",
        flex: 1,
      },
      {
        field: "total_pieces_delivered",
        headerName: "Pz. enviadas",
        flex: 1,
      },
      {
        field: "total_pieces_remaining",
        headerName: "Pz. restantes",
        flex: 1,
        renderCell: ({ row }) => {
          return row.total_pieces - row.total_pieces_delivered;
        },
      },
      {
        field: "sent_invoices_invoices",
        headerName: "Facturas",
        flex: 1,
        renderCell: ({ row }) => {
          const invoices = row?.sent_invoices;
          if (!invoices || invoices.length === 0) {
            return "---";
          }
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              {row.sent_invoices.map((invoice, key) => {
                const isLast = key === invoices.length - 1;
                return (
                  <div
                    style={{
                      flex: 1,
                      borderBottom: isLast ? "none" : "1px solid #ccc",
                      padding: "2px 0",
                    }}
                    key={`${row.order}-${invoice.invoice.id}-${key}`}
                  >
                    {invoice.invoice.invoice}
                  </div>
                );
              })}
            </div>
          );
        },
      },
      {
        field: "sent_invoices_pieces",
        headerName: "Pz. x factura",
        flex: 1,
        renderCell: ({ row }) => {
          const invoices = row?.sent_invoices;
          if (!invoices || invoices.length === 0) {
            return "---";
          }
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              {row.sent_invoices.map((invoice, key) => {
                const isLast = key === invoices.length - 1;
                return (
                  <div
                    style={{
                      flex: 1,
                      borderBottom: isLast ? "none" : "1px solid #ccc",
                      padding: "2px 0",
                    }}
                    key={`${row.order}-${invoice.invoice.id}-${invoice.qty}-${key}`}
                  >
                    {invoice.qty}
                  </div>
                );
              })}
            </div>
          );
        },
      },
    ],
    []
  );

  const { register, handleSubmit, reset, control } = useForm<
    IReportsSents,
    HttpError,
    Nullable<IReportsSentsFilterVariables>
  >({
    defaultValues: {
      q: getDefaultFilter("q", filters, "eq"),
      not_sent: false,
    },
  });

  const handleSearch = (params: any) => {
    const { q, not_sent } = params;
    const filters: CrudFilters = [];

    filters.push({
      operator: "or",
      value: [
        {
          field: "order",
          operator: "contains",
          value: q,
        },
        {
          field: "type",
          operator: "contains",
          value: q,
        },
        {
          field: "sent.sent_invoices.invoice.invoice",
          operator: "contains",
          value: q,
        },
      ],
    });
    if (not_sent) {
      filters.push({
        field: "not_sent",
        operator: "eq",
        value: not_sent,
      });
    }
    setFilters(filters);
  };

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      const el = document.querySelector(".MuiDataGrid-main");
      if (el) {
        const distanceFromTop = el.getBoundingClientRect().top;
        setHeaderHeight(distanceFromTop + 40);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, []);

  return (
    // <List>
    <List contentProps={{ sx: { height: "100%" } }}>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Card sx={{ paddingX: { xs: 2, md: 0 } }}>
            <CardContent sx={{ pt: 0 }}>
              <Box
                component="form"
                sx={{
                  display: "flex",
                  flexDirection: "column", // 👈 cambia de row a column
                  gap: 2,
                  alignItems: "flex-start", // alineados a la izquierda
                }}
                autoComplete="off"
                onSubmit={handleSubmit(handleSearch)}
              >
                {/* Input arriba */}
                <TextField
                  {...register("q")}
                  id="q"
                  label="Buscar"
                  placeholder="Ordenes, Tipo, Facturas"
                  margin="normal"
                  fullWidth
                  autoFocus
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlined />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Checkbox + Botones en una fila */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  <FormControlLabel
                    label="No enviados"
                    control={
                      <Controller
                        name="not_sent"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                    }
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    style={{ height: 40 }}
                  >
                    Buscar
                  </Button>

                  <Button
                    type="button"
                    variant="contained"
                    size="large"
                    color="error"
                    style={{ height: 40 }}
                    onClick={() => {
                      setFilters([]);
                      reset({ q: "", not_sent: false });
                    }}
                  >
                    Limpiar
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <DataGrid
        {...dataGridProps}
        autoHeight={false} // 👈 importante
        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        columns={columns}
        getRowHeight={() => "auto"}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
        }
        sx={{
          height: `calc(100vh - ${headerHeight}px)`,
          overflow: "auto",

          "& .MuiDataGrid-columnHeaders": {
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            zIndex: 10,
          },
          "& .MuiDataGrid-virtualScroller": {
            marginTop: "0px !important", // 👈 Forzamos quitar el margen
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            textOverflow: "clip",
            whiteSpace: "break-spaces",
            lineHeight: 1,
          },
          "& .even-row": {
            backgroundColor: "#f5f5f5",
            "&:hover": {
              backgroundColor: "#7b7979",
              color: "#fff",
            },
          },
          "& .odd-row": {
            backgroundColor: "#e0e0e0",
            "&:hover": {
              backgroundColor: "#7b7979",
              color: "#fff",
            },
          },
        }}
        hideFooter
      />
    </List>
  );
};
